import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, X, Rocket, Wrench, Bug, ChevronDown, ArrowRight } from "lucide-react";
import api from "../lib/api";

const fmt = (iso) => { try { return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }); } catch { return iso; } };

const Group = ({ icon: Icon, label, items, color }) => {
  if (!items || !items.length) return null;
  return (
    <div className="mt-3">
      <div className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide" style={{ color }}>
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <ul className="space-y-1.5">
        {items.map((t, i) => (
          <li key={i} className="flex gap-2 text-sm text-[#3f4a48] dark:text-gray-300">
            <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full" style={{ background: color }} /> <span>{t}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default function WhatsNew() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ releases: [], unread_count: 0 });
  const [expanded, setExpanded] = useState(0);

  const load = () => api.get("/whats-new").then((r) => setData(r.data)).catch(() => {});
  // Deferred: announcement content is never needed for first paint (keeps it off the critical path).
  useEffect(() => { const t = setTimeout(load, 3000); return () => clearTimeout(t); }, []);

  const openPanel = async () => {
    setOpen(true);
    if (data.unread_count > 0) {
      try { await api.post("/whats-new/seen"); setData((d) => ({ ...d, unread_count: 0 })); } catch {}
    }
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <>
      <button data-testid="whats-new-trigger" onClick={openPanel}
        className="relative flex w-full items-center gap-2 rounded-xl border border-[#E2E8F0] bg-white px-3 py-2.5 text-sm font-medium text-[#2D3436] transition-colors hover:border-[#0F766E]/40 hover:bg-[#0F766E]/5 dark:bg-transparent dark:text-gray-200">
        <Sparkles className="h-4 w-4 text-[#0F766E]" strokeWidth={1.7} /> What's New
        {data.unread_count > 0 && (
          <span data-testid="whats-new-unread" className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-[#0F766E] px-1.5 text-[11px] font-semibold text-white">{data.unread_count}</span>
        )}
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="whats-new-panel"
          role="dialog" aria-modal="true" aria-label="What's New in PlannioLife">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <div className="relative z-10 flex max-h-[85vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl bg-white shadow-2xl dark:bg-[#1f2937]">
            <div className="flex items-center justify-between border-b border-[#EBECE8] p-5 dark:border-gray-700">
              <div className="flex items-center gap-2">
                <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#0F766E]/10"><Sparkles className="h-5 w-5 text-[#0F766E]" /></span>
                <h3 className="font-display text-xl font-semibold text-[#2D3436] dark:text-white">What's New in PlannioLife</h3>
              </div>
              <button data-testid="whats-new-close" onClick={() => setOpen(false)} className="rounded-lg p-1.5 text-[#9aa3a0] hover:bg-black/5"><X className="h-5 w-5" /></button>
            </div>
            <div className="overflow-y-auto p-5">
              {data.releases.map((r, i) => (
                <div key={r.version} data-testid={`whats-new-release-${r.version}`} className="border-b border-[#F0F1EE] py-3 last:border-0 dark:border-gray-700">
                  <button onClick={() => setExpanded(expanded === i ? -1 : i)} className="flex w-full items-center gap-3 text-left">
                    <span className="rounded-full bg-[#0F766E]/10 px-2.5 py-0.5 text-xs font-semibold text-[#0F766E]">v{r.version}</span>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-[#2D3436] dark:text-white">{r.title}</div>
                      <div className="text-xs text-[#9aa3a0]">{fmt(r.date)}</div>
                    </div>
                    <ChevronDown className={`h-4 w-4 text-[#9aa3a0] transition-transform ${expanded === i ? "rotate-180" : ""}`} />
                  </button>
                  {expanded === i && (
                    <div className="mt-2 pl-1">
                      {r.summary && <p className="text-sm text-[#636E72] dark:text-gray-400">{r.summary}</p>}
                      <Group icon={Rocket} label="New" items={r.features} color="#7C3AED" />
                      <Group icon={Wrench} label="Improvements" items={r.improvements} color="#0F766E" />
                      <Group icon={Bug} label="Fixes" items={r.fixes} color="#B45309" />
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="border-t border-[#EBECE8] p-4 dark:border-gray-700">
              <Link to="/changelog" data-testid="whats-new-full-changelog" onClick={() => setOpen(false)}
                className="flex items-center justify-center gap-1.5 rounded-xl border border-[#E2E8F0] px-4 py-2.5 text-sm font-medium text-[#0F766E] transition-colors hover:bg-[#0F766E]/5 dark:border-gray-700">
                View the full changelog <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
